import React, { useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Radio,
  Stack,
} from "@mui/material";
import {
  Close,
  ArrowBackIosNew,
  ArrowForwardIos,
  LightbulbOutlined,
  CheckCircle,
  Cancel,
  EmojiEvents,
} from "@mui/icons-material";
import { QuizQuestion, useQuiz } from "./useQuiz";

// Mirrors the walkthrough's **bold** parser so authors can emphasize key
// terms in plain-string prompts without writing JSX.
const renderRichText = (content: React.ReactNode): React.ReactNode => {
  if (typeof content !== "string") return content;
  if (!content.includes("**")) return content;
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <Box
          key={i}
          component="strong"
          sx={{ color: "primary.main", fontWeight: 400 }}
        >
          {part.slice(2, -2)}
        </Box>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

export interface QuizProps {
  open: boolean;
  title: React.ReactNode;
  // Shown on the intro screen. Defaults to a generic copy line if omitted.
  introDescription?: React.ReactNode;
  questions: QuizQuestion[];
  passThreshold?: number;
  onClose: () => void;
}

const dialogPaperSx = {
  borderRadius: 3,
  boxShadow: "0 12px 32px -8px rgba(18,50,87,0.25)",
} as const;

const pillButtonSx = {
  textTransform: "none",
  borderRadius: 999,
  px: 3,
  fontWeight: 600,
} as const;

const ghostButtonSx = {
  color: "primary.main",
  textTransform: "none",
  fontWeight: 600,
  borderRadius: 999,
  px: 2,
} as const;

const Quiz: React.FC<QuizProps> = ({
  open,
  title,
  introDescription,
  questions,
  passThreshold,
  onClose,
}) => {
  const quiz = useQuiz(questions, { passThreshold });

  // Reset to intro whenever the dialog is closed so a re-open starts fresh.
  // The `reset` reference is stable enough for this purpose; we intentionally
  // depend only on `open`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) quiz.reset();
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"xs" as any}
      PaperProps={{ sx: { ...dialogPaperSx, width: "90vw", maxWidth: 560 } }}
    >
      <Box sx={{ p: 3 }}>
        <Header title={title} onClose={handleClose} />

        {quiz.phase === "intro" && (
          <IntroView
            description={introDescription}
            totalQuestions={quiz.totalQuestions}
            onStart={quiz.start}
            onSkip={handleClose}
          />
        )}

        {quiz.phase === "questions" && quiz.currentQuestion && (
          <QuestionView quiz={quiz} />
        )}

        {quiz.phase === "results" && (
          <ResultsView
            quiz={quiz}
            questions={questions}
            onClose={handleClose}
          />
        )}
      </Box>
    </Dialog>
  );
};

const Header: React.FC<{
  title: React.ReactNode;
  onClose: () => void;
}> = ({ title, onClose }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    mb={2}
  >
    <Box display="flex" alignItems="center" gap={1}>
      <LightbulbOutlined sx={{ color: "secondary.main", fontSize: 22 }} />
      <Typography
        sx={{
          fontSize: "1rem",
          fontWeight: 700,
          color: "primary.main",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </Typography>
    </Box>
    <IconButton size="small" onClick={onClose} aria-label="Close quiz">
      <Close fontSize="small" />
    </IconButton>
  </Box>
);

const IntroView: React.FC<{
  description?: React.ReactNode;
  totalQuestions: number;
  onStart: () => void;
  onSkip: () => void;
}> = ({ description, totalQuestions, onStart, onSkip }) => (
  <Box>
    <Typography
      sx={{ color: "primary.main", mb: 1, fontWeight: 600, fontSize: "1rem" }}
    >
      You finished the walkthrough!
    </Typography>
    <Typography sx={{ color: "text.secondary", mb: 3 }}>
      {description ??
        `Take a quick ${totalQuestions}-question check to confirm you've got it. ~1 minute.`}
    </Typography>
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button onClick={onSkip} sx={ghostButtonSx}>
        Maybe later
      </Button>
      <Button onClick={onStart} variant="contained" sx={pillButtonSx}>
        Start Quiz
      </Button>
    </Stack>
  </Box>
);

const QuestionView: React.FC<{
  quiz: ReturnType<typeof useQuiz>;
}> = ({ quiz }) => {
  const q = quiz.currentQuestion;
  if (!q) return null;
  const progress = ((quiz.currentQuestionIndex + 1) / quiz.totalQuestions) * 100;

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Chip
          label={`QUESTION ${quiz.currentQuestionIndex + 1} OF ${quiz.totalQuestions}`}
          size="small"
          sx={{
            bgcolor: "primary.main",
            color: "common.white",
            fontWeight: 700,
            fontSize: "0.7rem",
            letterSpacing: "0.05em",
            borderRadius: 999,
            height: 22,
          }}
        />
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          {quiz.currentQuestionIndex + 1} / {quiz.totalQuestions}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 4,
          borderRadius: 999,
          mb: 2.5,
          bgcolor: "rgba(18,50,87,0.08)",
          "& .MuiLinearProgress-bar": { borderRadius: 999 },
        }}
      />

      <Typography
        sx={{
          fontSize: "1rem",
          fontWeight: 700,
          color: "primary.main",
          mb: 2,
          lineHeight: 1.4,
        }}
      >
        {renderRichText(q.prompt)}
      </Typography>

      <Stack spacing={1} mb={2}>
        {q.options.map((opt) => {
          const isAnswered = quiz.selectedOptionId !== undefined;
          const isSelected = quiz.selectedOptionId === opt.id;
          const isCorrectOption = opt.id === q.correctOptionId;

          // Three post-answer visual states:
          //  - the correct option (always green w/ check)
          //  - the user's wrong pick (red w/ X)
          //  - everything else (muted gray)
          // Pre-answer: plain card with coral hover.
          let borderColor: string = "divider";
          let bgcolor: string = "background.paper";
          let labelColor: string = "primary.main";
          let leadingIcon: React.ReactNode = (
            <Radio
              checked={false}
              size="small"
              sx={{ p: 0, color: "primary.main" }}
            />
          );

          if (isAnswered) {
            if (isCorrectOption) {
              borderColor = "success.main";
              bgcolor = "rgba(46,125,50,0.08)";
              labelColor = "success.dark";
              leadingIcon = (
                <CheckCircle sx={{ color: "success.main", fontSize: 22 }} />
              );
            } else if (isSelected) {
              borderColor = "error.main";
              bgcolor = "rgba(211,47,47,0.08)";
              labelColor = "error.dark";
              leadingIcon = (
                <Cancel sx={{ color: "error.main", fontSize: 22 }} />
              );
            } else {
              labelColor = "text.disabled";
              leadingIcon = (
                <Radio
                  checked={false}
                  size="small"
                  sx={{ p: 0, color: "text.disabled" }}
                />
              );
            }
          }

          return (
            <Paper
              key={opt.id}
              elevation={0}
              onClick={() => {
                if (isAnswered) return;
                quiz.selectAnswer(opt.id);
              }}
              sx={{
                p: 1.25,
                pl: 1.5,
                borderRadius: 2,
                border:
                  isAnswered && (isCorrectOption || isSelected)
                    ? "1.5px solid"
                    : "1px solid",
                borderColor,
                bgcolor,
                cursor: isAnswered ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                transition: "all 0.15s ease",
                ...(isAnswered
                  ? {}
                  : {
                      "&:hover": {
                        bgcolor: "rgba(255,95,71,0.03)",
                        borderColor: "secondary.main",
                      },
                    }),
              }}
            >
              {leadingIcon}
              <Typography sx={{ color: labelColor, fontWeight: 500 }}>
                {renderRichText(opt.label)}
              </Typography>
            </Paper>
          );
        })}
      </Stack>

      {/* Inline explanation revealed after the user commits an answer.
          Authors put rationale in `explanation`; surfacing it here keeps the
          learning value now that the results screen no longer lists each
          wrong question. */}
      {quiz.selectedOptionId !== undefined && q.explanation && (
        <Box
          sx={{
            p: 1.5,
            mb: 3,
            borderRadius: 2,
            bgcolor: "rgba(18,50,87,0.04)",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              display: "block",
              mb: 0.5,
            }}
          >
            Why
          </Typography>
          <Typography variant="body2" sx={{ color: "primary.main" }}>
            {renderRichText(q.explanation)}
          </Typography>
        </Box>
      )}
      {quiz.selectedOptionId === undefined && <Box sx={{ mb: 1 }} />}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Button
          startIcon={
            <ArrowBackIosNew sx={{ fontSize: "14px !important" }} />
          }
          onClick={quiz.previous}
          disabled={quiz.currentQuestionIndex === 0}
          sx={ghostButtonSx}
        >
          Previous
        </Button>
        {quiz.isLastQuestion ? (
          <Button
            onClick={quiz.submit}
            disabled={!quiz.selectedOptionId}
            variant="contained"
            sx={pillButtonSx}
          >
            Submit
          </Button>
        ) : (
          <Button
            onClick={quiz.next}
            disabled={!quiz.selectedOptionId}
            endIcon={
              <ArrowForwardIos sx={{ fontSize: "14px !important" }} />
            }
            variant="contained"
            sx={pillButtonSx}
          >
            Next
          </Button>
        )}
      </Stack>
    </Box>
  );
};

const ResultsView: React.FC<{
  quiz: ReturnType<typeof useQuiz>;
  questions: QuizQuestion[];
  onClose: () => void;
}> = ({ quiz, questions, onClose }) => {
  // Per-question feedback (green/red + Why) is shown inline during the quiz,
  // so the results screen focuses on the headline outcome: big circular
  // score, encouraging copy, and a hit/miss row that maps to the questions.
  const percentage = Math.round((quiz.score / quiz.totalQuestions) * 100);
  const accentColor = quiz.passed ? "success.main" : "warning.main";
  const accentTint = quiz.passed
    ? "rgba(46,125,50,0.10)"
    : "rgba(237,108,2,0.10)";
  const accentRing = quiz.passed
    ? "rgba(46,125,50,0.25)"
    : "rgba(237,108,2,0.25)";

  return (
    <Box>
      {/* Big score circle with a soft outer halo */}
      <Box display="flex" justifyContent="center" mb={2.5}>
        <Box
          sx={{
            position: "relative",
            width: 152,
            height: 152,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // outer halo
            "&::before": {
              content: '""',
              position: "absolute",
              inset: -8,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: accentRing,
            },
          }}
        >
          <Box
            sx={{
              width: 132,
              height: 132,
              borderRadius: "50%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: accentTint,
              border: "3px solid",
              borderColor: accentColor,
            }}
          >
            {quiz.passed && (
              <EmojiEvents sx={{ color: accentColor, fontSize: 26, mb: 0.25 }} />
            )}
            <Typography
              sx={{
                fontSize: "1.9rem",
                fontWeight: 800,
                color: accentColor,
                lineHeight: 1,
                letterSpacing: "-0.02em",
              }}
            >
              {quiz.score} / {quiz.totalQuestions}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: accentColor,
                mt: 0.5,
                letterSpacing: "0.04em",
              }}
            >
              {percentage}%
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Headline + supporting copy */}
      <Typography
        sx={{
          textAlign: "center",
          color: "primary.main",
          fontWeight: 700,
          fontSize: "1.15rem",
          letterSpacing: "-0.01em",
        }}
      >
        {quiz.passed ? "Nice work!" : "Almost there!"}
      </Typography>
      <Typography
        sx={{
          textAlign: "center",
          color: "text.secondary",
          mb: 2.5,
          fontSize: "0.9rem",
        }}
      >
        {quiz.passed
          ? "You've got the basics down."
          : `Pass mark is ${quiz.passThreshold} / ${quiz.totalQuestions}. Give it another go.`}
      </Typography>

      {/* Per-question hit/miss row — small dots in question order */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="center"
        mb={3}
        aria-label="Per-question results"
      >
        {questions.map((q) => {
          const isCorrect = quiz.answers[q.id] === q.correctOptionId;
          return (
            <Box
              key={q.id}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: isCorrect
                  ? "rgba(46,125,50,0.12)"
                  : "rgba(211,47,47,0.12)",
                color: isCorrect ? "success.main" : "error.main",
              }}
            >
              {isCorrect ? (
                <CheckCircle sx={{ fontSize: 18 }} />
              ) : (
                <Cancel sx={{ fontSize: 18 }} />
              )}
            </Box>
          );
        })}
      </Stack>

      {/* Buttons */}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        {!quiz.passed && (
          <Button onClick={quiz.retry} variant="contained" sx={pillButtonSx}>
            Retry
          </Button>
        )}
        <Button
          onClick={onClose}
          variant={quiz.passed ? "contained" : "outlined"}
          sx={pillButtonSx}
        >
          Close
        </Button>
      </Stack>
    </Box>
  );
};

export default Quiz;
